import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import LazyLoad from "react-lazyload";
import { Link } from "react-router-dom";
import styled from "styled-components";
import sanityClient from "../../sanityClient";
import tempImage from "../../assets/images/temp.jpg";
import groupPhoto from "../../assets/images/who-we-are/group-photo.webp";

const MeetHabitatBuffalo = () => {
  const [selectedTab, setSelectedTab] = useState("Board");
  const [teamData, setTeamData] = useState({
    Board: [],
    Leadership: [],
    Administrative: [],
    FamilyServices: [],
    Development: [],
    Construction: [],
    ReStore: [],
    StrategicInitiatives: [],
  });

  useEffect(() => {
    sanityClient
      .fetch(
        `*[_type == "team"]{
          _id,
          name,
          role,
          bio,
          department,
          isLeader,
          affiliation,
          "imageUrl": image.asset->url
        }`
      )
      .then((data) => {
        const groupedData = {
          Board: [],
          Leadership: [],
          Administrative: [],
          FamilyServices: [],
          Development: [],
          Construction: [],
          ReStore: [],
          StrategicInitiatives: [], // Ensure this department is included in the grouping
        };

        data.forEach((member) => {
          if (groupedData[member.department]) {
            groupedData[member.department].push(member);
          }
        });

        // Sort each department by isLeader first and then by Manager title, except Board
        for (let key in groupedData) {
          if (key !== "Board") {
            groupedData[key].sort((a, b) => {
              if (a.isLeader !== b.isLeader) {
                return a.isLeader ? -1 : 1;
              }
              if (a.role.includes("Manager") && !b.role.includes("Manager")) {
                return -1;
              }
              if (!a.role.includes("Manager") && b.role.includes("Manager")) {
                return 1;
              }
              return 0;
            });
          }
        }

        setTeamData(groupedData);
      })
      .catch(console.error);
  }, []);

  const formatTabName = (tab) => {
    if (tab === "ReStore") {
      return "ReStore"; // Keep "ReStore" as is
    }
    return tab.replace(/([A-Z])/g, " $1").trim(); // Add space before each uppercase letter for other tabs
  };

  const renderTeamSection = (team, tabName) => {
    const leader = team.find((member) => member.isLeader);
    const members = team.filter((member) => !member.isLeader);

    if (tabName === "Board") {
      const executiveTeam = members.filter(
        (member) => member.role && member.role !== "Board Member"
      );
      const boardMembers = members.filter(
        (member) => !member.role || member.role === "Board Member"
      );

      return (
        <TwoColumnWrapper>
          {leader && (
            <Leader>
              <LazyLoad height={150} offset={100}>
                <img
                  src={leader.imageUrl || tempImage}
                  alt={leader.name}
                  className="headshot"
                />
              </LazyLoad>
              <div className="info">
                <h3>{leader.name}</h3>
                <Affiliation>{leader.affiliation}</Affiliation>
                <Role>{leader.role}</Role>
                <p>{leader.bio}</p>
              </div>
            </Leader>
          )}
          <MembersSection>
            <SectionTitle>Executive Team</SectionTitle>
            <Members className={`members-${executiveTeam.length}`}>
              {executiveTeam.map((member) => (
                <MemberItem key={member._id}>
                  <h3>{member.name}</h3>
                  <Affiliation>{member.affiliation}</Affiliation>
                  <Role>{member.role}</Role>
                </MemberItem>
              ))}
            </Members>
            <SectionTitle>Board Members</SectionTitle>
            <Members className={`members-${boardMembers.length}`} isBoard>
              {boardMembers.map((member) => (
                <MemberItem key={member._id}>
                  <h3>{member.name}</h3>
                  <Affiliation>{member.affiliation}</Affiliation>
                  <Role>{member.role}</Role>
                </MemberItem>
              ))}
            </Members>
          </MembersSection>
        </TwoColumnWrapper>
      );
    }

    return (
      <TwoColumnWrapper>
        {leader && (
          <Leader>
            <LazyLoad height={150} offset={100}>
              <img
                src={leader.imageUrl || tempImage}
                alt={leader.name}
                className="headshot"
              />
            </LazyLoad>
            <div className="info">
              <h3>{leader.name}</h3>
              <Affiliation>{leader.affiliation}</Affiliation>
              <Role>{leader.role}</Role>
              <p>{leader.bio}</p>
            </div>
          </Leader>
        )}
        <MembersSection>
          <Members className={`members-${members.length}`}>
            {members.map((member) => (
              <MemberItem key={member._id}>
                <h3>{member.name}</h3>
                <Affiliation>{member.affiliation}</Affiliation>
                <Role>{member.role}</Role>
              </MemberItem>
            ))}
          </Members>
        </MembersSection>
      </TwoColumnWrapper>
    );
  };

  return (
    <>
      <Helmet>
        <title>Meet the Habitat Buffalo Team</title>
        <meta
          name="description"
          content="Get to know the dedicated team members behind Habitat for Humanity Buffalo. Learn about our board, leadership, administrative, family services, development, construction, ReStore, and strategic initiatives teams."
        />
      </Helmet>
      <Container className="container">
        <h1>Meet Habitat Buffalo</h1>
        <GroupPhoto>
          <LazyLoad height={400} offset={100}>
            <img src={groupPhoto} alt="Habitat Buffalo Team" />
          </LazyLoad>
        </GroupPhoto>
        <Intro>
          Our team is dedicated to the mission of building homes, communities,
          and hope. Each member plays a crucial role in helping Habitat for
          Humanity Buffalo achieve its goals. Get to know the people behind the
          mission.
        </Intro>
        <Tabs>
          {Object.keys(teamData).map((tab) => (
            <Tab
              key={tab}
              active={selectedTab === tab}
              onClick={() => setSelectedTab(tab)}
            >
              {formatTabName(tab)}
            </Tab>
          ))}
        </Tabs>
        {Object.keys(teamData).map((tab) => (
          <TeamSection key={tab} active={selectedTab === tab}>
            <h2 className="section-header">{formatTabName(tab)}</h2>
            <p>
              {tab === "Board" &&
                "Our Board of Directors is composed of dedicated volunteers from the community who provide guidance and oversight to ensure Habitat Buffalo achieves its mission."}
              {tab === "Leadership" &&
                "Our Leadership Team guides the organization and ensures that our mission and vision are being effectively pursued."}
              {tab === "Administrative" &&
                "The Administrative Team supports the daily operations and ensures smooth functioning of our programs."}
              {tab === "FamilyServices" &&
                "The Family Services Team works directly with our partner families to support them through the homeownership process."}
              {tab === "Development" &&
                "The Development Team focuses on fundraising, communications, and volunteer management to support Habitat Buffalo's mission."}
              {tab === "Construction" &&
                "The Construction Team oversees the building and renovation of Habitat homes, ensuring quality and safety."}
              {tab === "ReStore" &&
                "The ReStore Team manages our retail operations, which support Habitat Buffalo's mission through the sale of donated goods."}
              {tab === "StrategicInitiatives" &&
                "The Strategic Initiatives Team is focused on long-term planning and special projects that drive the mission forward."}
            </p>
            {renderTeamSection(teamData[tab], tab)}
          </TeamSection>
        ))}
        <JoinSection>
          <JoinTitle>Join the Habitat Buffalo Team</JoinTitle>
          <JoinText>
            Interested in making a difference? Explore our career opportunities
            and join us in building a better future.
          </JoinText>
          <JoinButton to="/who-we-are/career-opportunities">
            View Career Opportunities
          </JoinButton>
        </JoinSection>
      </Container>
    </>
  );
};

const Container = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
`;

const GroupPhoto = styled.div`
  text-align: center;
  margin-bottom: 20px;

  img {
    width: 100%;
    max-width: 800px;
    height: auto;
    border-radius: 10px;
  }
`;

const Intro = styled.p`
  text-align: center;
  margin-bottom: 40px;
`;

const Tabs = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between; /* Ensures even spacing */
  margin-bottom: 20px;
  gap: 10px; /* Add some space between tabs */

  @media (max-width: 768px) {
    justify-content: center; /* Center tabs on smaller screens */
  }
`;

const Tab = styled.button`
  flex: 1 1 calc(25% - 10px); /* Adjust width and spacing */
  background-color: ${({ active }) => (active ? "#00ACD8" : "transparent")};
  color: ${({ active }) => (active ? "#fff" : "#000")};
  border: ${({ active }) => (active ? "none" : "1px solid #ccc")};
  padding: 10px;
  cursor: pointer;
  margin: 5px;
  border-radius: 5px;
  min-width: 120px; /* Set a minimum width for each tab */

  &:hover {
    background-color: #00acd8;
    color: #fff;
  }

  @media (max-width: 768px) {
    flex: 1 1 45%; /* Adjust for smaller screens */
  }
`;

const TeamSection = styled.section`
  display: ${({ active }) => (active ? "block" : "none")};
  margin-bottom: 40px;

  .section-header {
    text-align: center;
    background-color: var(--hhb-primary);
    color: var(--hhb-white);
    padding: 10px;
    border-radius: 5px;
    margin-bottom: 20px;
  }
`;

const SectionTitle = styled.h3`
  font-size: 1.5em;
  margin-bottom: 10px;
  text-align: center;
`;

const TwoColumnWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 20px;
  align-items: flex-start;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Leader = styled.div`
  text-align: center;

  img {
    width: 150px;
    height: 150px;
    border-radius: 50%;
    object-fit: cover;
  }

  .info {
    margin-top: 10px;

    h3 {
      margin: 0;
      font-size: 1.5em;
      font-weight: bold;
    }

    p {
      margin: 5px 0;
    }
  }
`;

const Role = styled.p`
  font-style: italic;
  margin-bottom: 5px;
  font-size: 1em;
`;

const Affiliation = styled.p`
  font-weight: bold;
  color: #555;
  font-size: 0.9em;
`;

const MembersSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
`;

const Members = styled.div`
  display: grid;
  grid-template-columns: repeat(
    ${({ className }) => Math.min(Math.ceil(className.split("-")[1] / 2), 4)},
    1fr
  );
  gap: 20px;
  justify-items: center;
  width: 100%;
`;

const MemberItem = styled.div`
  text-align: center;

  h3 {
    font-size: 1em;
    margin: 0;
    font-weight: bold;
  }

  p {
    margin: 5px 0;
    font-size: 0.9em;
  }
`;

const JoinSection = styled.div`
  text-align: center;
  margin-top: 40px;
`;

const JoinTitle = styled.h2`
  font-size: 2em;
  margin-bottom: 10px;
`;

const JoinText = styled.p`
  font-size: 1.2em;
  margin-bottom: 20px;
`;

const JoinButton = styled(Link)`
  background-color: var(--hhb-primary);
  color: var(--hhb-white);
  padding: 10px 20px;
  text-decoration: none;
  border-radius: 5px;
  font-size: 1em;
  transition: background-color 0.3s;

  &:hover {
    background-color: var(--hhb-darkblue);
  }
`;

export default MeetHabitatBuffalo;
